import { useBlocker } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { useDialog } from 'features/modalManager';
import { t } from '@lingui/macro';
import { useTheme } from '@mui/material';

type NavigationGuardOptions = {
  shouldBlock: boolean;
  onProceed?: () => void;
  /**
   * Raised true while the confirmation dialog is open so editing cells skip the
   * commit/exit that the dialog's focus-steal would otherwise trigger (DL-60097).
   */
  navBlockingRef?: MutableRefObject<boolean>;
};

/**
 * Blocks route navigation and browser close/refresh when shouldBlock is true.
 * Shows a confirmation dialog via the modal manager when the user tries to navigate away.
 *
 * IMPORTANT: This hook calls useBlocker, which requires a data router context
 * (createBrowserRouter + RouterProvider). Use NavigationGuard component wrapper
 * for safe usage outside data router (e.g. Storybook).
 */
export function useNavigationGuard({
  shouldBlock,
  onProceed,
  navBlockingRef,
}: NavigationGuardOptions) {
  // react-router 6.20's useBlocker does not reliably re-arm after proceed()/reset() — the
  // dialog would only ever appear once. After the user responds we briefly suspend the
  // blocker (registering useBlocker(false)) and re-enable it on the next tick, which forces
  // a fresh blocker so subsequent navigations are intercepted again (DL-60097).
  const [blockerSuspended, setBlockerSuspended] = useState(false);
  const blocker = useBlocker(shouldBlock && !blockerSuspended);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  useEffect(() => {
    if (!blockerSuspended) return undefined;
    const timeout = window.setTimeout(() => setBlockerSuspended(false), 0);
    return () => window.clearTimeout(timeout);
  }, [blockerSuspended]);

  const onProceedRef = useRef(onProceed);
  onProceedRef.current = onProceed;

  const navBlockingRefInner = useRef(navBlockingRef);
  navBlockingRefInner.current = navBlockingRef;

  const { openDialog } = useDialog();
  const theme = useTheme();

  // Raise the nav-blocking flag when navigation is blocked. Covers paths with no preceding
  // nav-link click (e.g. browser back): the dialog steals focus → the editing input blurs →
  // the cell reads this flag and skips the save (DL-60097).
  useLayoutEffect(() => {
    if (blocker.state === 'blocked' && navBlockingRefInner.current) {
      navBlockingRefInner.current.current = true;
    }
  }, [blocker.state]);

  // Suppress the auto-save when the user clicks an in-app navigation link while editing.
  // Clicking such a link blurs the editing input on mousedown — committing the edit (an
  // immediate server PUT) — BEFORE the click lets react-router block, so the change would be
  // saved before the dialog appears and "Leave"/Esc could not discard it. A capture-phase
  // pointerdown runs before that blur and (using the clicked element, reliable cross-browser)
  // raises the skip-flag, so the imminent blur-save is skipped and nothing is sent. If no
  // navigation is actually blocked (e.g. clicking the already-selected module), the flag is
  // cleared on the next tick so normal saves are unaffected (DL-60097).
  useEffect(() => {
    if (!shouldBlock) return undefined;
    const onPointerDownCapture = (e: PointerEvent) => {
      const ref = navBlockingRefInner.current;
      if (!ref) return;
      const { target } = e;
      if (!(target instanceof Element) || !target.closest('a[href]')) return;
      ref.current = true;
    };
    // Release the flag once the click's navigation attempt has settled — unless a block is in
    // effect. Scheduled from the *click* (not the pointerdown) so it runs AFTER cells that
    // commit on click-away (e.g. multiselect/dropdown) have read the flag, and after
    // react-router has decided whether to block. Scheduling from pointerdown could reset the
    // flag before a click-driven commit ran, so that commit would still go through (DL-60097).
    const onClickCapture = () => {
      const ref = navBlockingRefInner.current;
      if (!ref?.current) return;
      window.setTimeout(() => {
        if (blockerRef.current.state !== 'blocked') ref.current = false;
      }, 0);
    };
    document.addEventListener('pointerdown', onPointerDownCapture, true);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [shouldBlock]);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    openDialog({
      id: 'navigate-away-confirm',
      minimize: true,
      variant: 'leftAligned',
      slotProps: { paper: { sx: { maxWidth: '480px' } } },
      title: t`Leave without saving?`,
      description: t`Your changes to this field won't be saved.`,
      hideDontShowAgain: true,
      actions: [
        { label: t`Stay`, variant: 'secondary', id: 'cancel' },
        {
          label: t`Leave`,
          variant: 'primary',
          id: 'apply',
          sx: { backgroundColor: theme.palette.bg.errorSolid },
        },
      ],
    }).then(result => {
      if (result === 'apply') {
        onProceedRef.current?.();
        blockerRef.current.proceed?.();
      } else {
        // Stay: the cell was kept in edit mode. MUI returns focus to whatever opened the
        // dialog (the nav link), not our input — each editing cell pulls focus back to its
        // own input via an effect tied to isEditing.
        blockerRef.current.reset?.();
        // Force the blocker to re-register so the dialog can appear again next time.
        setBlockerSuspended(true);
      }
      // Reset the nav-blocking flag on the next macrotask. The Stay/Leave button click
      // bubbles up to document AFTER this promise's microtask, so document-level
      // click-away listeners (used by Dropdown / Multi-select / Date poppers) still
      // need the flag to read true while that click is being delivered — otherwise
      // they'd run their commit and close the editor right after "Stay" (DL-60097).
      window.setTimeout(() => {
        if (navBlockingRefInner.current) navBlockingRefInner.current.current = false;
      }, 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openDialog and theme are stable refs, only re-run when blocker state changes
  }, [blocker.state]);

  // Block browser close/refresh with native beforeunload prompt
  useEffect(() => {
    if (!shouldBlock) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [shouldBlock]);
}
